import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useIsAdmin } from '../auth/AuthContext'
import { confirmAndDeleteBatch } from '../utils/batchDelete'
import { Progress, StatusBadge } from '../components/BatchStatus'
import { theme } from '../theme'
import { fmtDateTime } from '../utils/datetime'
import { jobOutboundLabel } from '../utils/methods'

/**
 * Tactical E2E — launch a burst of N faxes and watch it land.
 *
 * This exists because scheduled monitoring structurally cannot answer "what happens under
 * load": it sends one fax at a time, so it never presents the platform with concurrency.
 *
 * Polls every 5s while open. Polling rather than SSE/WebSocket is deliberate — the app runs
 * two gunicorn workers with no shared pub/sub and no sticky routing, so a stream that landed
 * on the worker which did not dispatch the batch would silently show nothing. A poll loop
 * cannot fail silently.
 */

const POLL_MS = 5000
const SIZES = [5, 10, 25, 50]

type Batch = {
  id: string
  job_id: string
  job_name?: string | null
  size: number
  sla_pass_pct: number
  stagger_ms: number
  status: string
  t_created?: string | null
  t_dispatch_complete?: string | null
  t_completed?: string | null
  queued: number
  in_flight: number
  terminal: number
}

// Comes straight off GET /api/jobs, so it holds the STORED method value
// ('eapi', 'smtp', …). Never render it raw, and never render it through a
// method-only label either: the stored values are SHARED across brands, so
// 'eapi' alone cannot say whose eAPI it is. `outbound_provider` is the field
// that answers that, and it is carried here for exactly that reason — the
// picker reads BOTH and asks `jobOutboundLabel` to compose them.
type Job = {
  id: string
  name: string
  enabled: boolean
  outbound_method: string
  // Absent renders as the bare transport, never as eFax (PROVIDER_NOT_RECORDED).
  outbound_provider?: string | null
}

// `StatusBadge` and `Progress` are NOT defined here. They were, in-file and unexported, while
// the internal batch page rendered the raw status string instead — the divergence design.md
// §8.2 asked for a shared module to prevent. Both pages now render through
// `components/BatchStatus.tsx`, and a guard asserts neither page can stop doing so.

export default function TacticalE2E() {
  const isAdmin = useIsAdmin()
  const navigate = useNavigate()
  const [batches, setBatches] = useState<Batch[]>([])
  const [jobs, setJobs] = useState<Job[]>([])
  const [err, setErr] = useState<string | null>(null)
  const [launching, setLaunching] = useState(false)
  const [showLaunch, setShowLaunch] = useState(false)
  const [deleting, setDeleting] = useState<string | null>(null)

  const [jobId, setJobId] = useState('')
  const [size, setSize] = useState(5)
  const [passPct, setPassPct] = useState(95)
  const [staggerMs, setStaggerMs] = useState(2000)

  const load = useCallback(async () => {
    try {
      const r = await fetch('/api/batches?limit=50')
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const d = await r.json()
      setBatches(d.batches || [])
      setErr(null)
    } catch (e) {
      // Surfaced, not swallowed: a silent fetch failure leaves a page that looks merely
      // idle, which is indistinguishable from "no batches yet".
      setErr(e instanceof Error ? e.message : String(e))
    }
  }, [])

  useEffect(() => {
    load()
    fetch('/api/jobs')
      .then(r => r.json())
      .then(d => setJobs((d.jobs || d || []).filter((j: Job) => j.enabled)))
      .catch(() => {})
    const t = setInterval(load, POLL_MS)
    return () => clearInterval(t)
  }, [load])

  // Whole-batch delete. The confirmation sequence and the endpoint call live in
  // `utils/batchDelete` because the dashboard's batch rows now offer the same action, and a
  // duplicated ESCALATION decision is one that gets tightened here and stays lax there. See that
  // module for why the second prompt exists: an outstanding fax has already been handed to the
  // provider and will arrive, so deleting its row first files the arrival as UNMATCHED -- which
  // manufactures the exact signal a burst test is run to measure.
  async function deleteBatch(b: Batch) {
    setDeleting(b.id)
    setErr(null)
    try {
      const outcome = await confirmAndDeleteBatch({
        id: b.id,
        label: `${b.size} faxes on ${b.job_name || b.job_id.slice(0, 8)}`,
      })
      if (outcome.error) {
        setErr(outcome.error)
        return
      }
      if (!outcome.deleted) return
      setBatches(prev => prev.filter(x => x.id !== b.id))
      await load()
    } finally {
      setDeleting(null)
    }
  }

  async function launch() {
    setLaunching(true)
    setErr(null)
    // Held outside the try so the navigation can happen AFTER the finally block has
    // cleared `launching` (G1.2). Null on any failure, which is what keeps a failed
    // launch on the list page with its inline error (G1.3).
    let newBatchId: string | null = null
    try {
      const r = await fetch('/api/batches', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ job_id: jobId, size, sla_pass_pct: passPct, stagger_ms: staggerMs }),
      })
      if (!r.ok) {
        const body = await r.json().catch(() => ({}))
        throw new Error(body.detail || `HTTP ${r.status}`)
      }
      // The new batch's id is already in the 201 body — BatchResponse.id,
      // backend/api/batches.py. No backend change is needed for this (G1.5).
      const created = await r.json().catch(() => ({}))
      newBatchId = typeof created.id === 'string' && created.id ? created.id : null
      setShowLaunch(false)
      // Reloading the list is a wasted round trip when we are about to leave it — the
      // detail page fetches the batch itself. Refresh only when there is no id to go to,
      // so a body we could not parse still degrades to the old stay-on-the-list behaviour.
      if (!newBatchId) await load()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setLaunching(false)
    }
    // Form dismissed and `launching` cleared above, so returning to the list from the
    // detail page does not show a stale open form mid-submit (G1.2). The batch exists the
    // moment the POST returns — dispatch is adopted by the leader's monitor within one 5s
    // cycle — so the detail page is correct to visit immediately (G1.1).
    if (newBatchId) navigate(`/tactical/${newBatchId}`)
  }

  const estSeconds = Math.round((size * staggerMs) / 1000)

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
        <h2 style={{ margin: 0 }}>Tactical E2E</h2>
        {isAdmin && !showLaunch && (
          <button
            onClick={() => setShowLaunch(true)}
            style={{
              padding: '0.5rem 1rem', background: theme.primary, color: '#fff', border: 'none',
              borderRadius: 4, cursor: 'pointer', fontWeight: 600,
            }}
          >
            Launch batch
          </button>
        )}
      </div>

      <p style={{ color: '#9aa0a6', fontSize: '0.82rem', marginTop: 0, maxWidth: '46rem' }}>
        Sends N faxes at once against a job's live configuration, to measure the platform
        under burst load.
      </p>

      {err && (
        <div style={{ background: '#3a1e1e', border: '1px solid #6a2a2a', borderRadius: 6, padding: '0.7rem', marginBottom: '1rem' }}>
          <span style={{ color: '#ef9a9a', fontSize: '0.82rem' }}>{err}</span>
        </div>
      )}

      {showLaunch && (
        <div style={{ background: theme.bgAlt, border: `1px solid ${theme.border}`, borderRadius: 6, padding: '1rem', marginBottom: '1.2rem' }}>
          <h3 style={{ margin: '0 0 0.8rem', fontSize: '0.95rem' }}>Launch a batch</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(11rem, 1fr))', gap: '0.9rem' }}>
            <label style={{ fontSize: '0.78rem', color: '#9aa0a6' }}>
              Job
              <select
                value={jobId}
                onChange={e => setJobId(e.target.value)}
                style={{ width: '100%', marginTop: '0.25rem', padding: '0.4rem', background: theme.bg, color: theme.text, border: `1px solid ${theme.border}`, borderRadius: 4 }}
              >
                <option value="">Select a job…</option>
                {jobs.map(j => (
                  <option key={j.id} value={j.id}>
                    {j.name} ({jobOutboundLabel(j.outbound_provider, j.outbound_method)})
                  </option>
                ))}
              </select>
            </label>

            <label style={{ fontSize: '0.78rem', color: '#9aa0a6' }}>
              Size
              <select
                value={size}
                onChange={e => setSize(Number(e.target.value))}
                style={{ width: '100%', marginTop: '0.25rem', padding: '0.4rem', background: theme.bg, color: theme.text, border: `1px solid ${theme.border}`, borderRadius: 4 }}
              >
                {SIZES.map(s => <option key={s} value={s}>{s} faxes</option>)}
              </select>
            </label>

            <label style={{ fontSize: '0.78rem', color: '#9aa0a6' }}>
              Pass threshold (%)
              <input
                type="number" min={1} max={100} value={passPct}
                onChange={e => setPassPct(Number(e.target.value))}
                style={{ width: '100%', marginTop: '0.25rem', padding: '0.4rem', background: theme.bg, color: theme.text, border: `1px solid ${theme.border}`, borderRadius: 4 }}
              />
            </label>

            <label style={{ fontSize: '0.78rem', color: '#9aa0a6' }}>
              Stagger (ms)
              <input
                type="number" min={0} max={60000} step={250} value={staggerMs}
                onChange={e => setStaggerMs(Number(e.target.value))}
                style={{ width: '100%', marginTop: '0.25rem', padding: '0.4rem', background: theme.bg, color: theme.text, border: `1px solid ${theme.border}`, borderRadius: 4 }}
              />
            </label>
          </div>

          {/* The cost is stated before the button, not after. Every item is a real fax with
              real per-page billing, and a quality-checked job adds a second page to each. */}
          <div style={{ fontSize: '0.76rem', color: '#ffa726', marginTop: '0.9rem' }}>
            This will send {size} real fax{size === 1 ? '' : 'es'}, taking about {estSeconds}s
            to submit them all. A batch passes a leg when at least {passPct}% of its
            evaluable items met that leg's SLA.
          </div>

          <div style={{ display: 'flex', gap: '0.6rem', marginTop: '0.9rem' }}>
            <button
              onClick={launch}
              disabled={!jobId || launching}
              style={{
                padding: '0.5rem 1.1rem', background: jobId ? theme.primary : theme.bgElevated,
                color: jobId ? '#fff' : '#666', border: 'none', borderRadius: 4,
                cursor: jobId && !launching ? 'pointer' : 'default', fontWeight: 600,
              }}
            >
              {launching ? 'Launching…' : `Send ${size} faxes`}
            </button>
            <button
              onClick={() => setShowLaunch(false)}
              style={{ padding: '0.5rem 0.9rem', background: 'transparent', color: '#9aa0a6', border: `1px solid ${theme.border}`, borderRadius: 4, cursor: 'pointer' }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {batches.length === 0 ? (
        <p style={{ color: '#9aa0a6', fontSize: '0.85rem' }}>
          No batches yet.{isAdmin ? ' Launch one to measure burst behaviour.' : ''}
        </p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
          <thead>
            <tr style={{ background: theme.bgElevated, textAlign: 'left' }}>
              <th style={{ padding: '0.5rem' }}>Started</th>
              <th style={{ padding: '0.5rem' }}>Job</th>
              <th style={{ padding: '0.5rem' }}>Size</th>
              <th style={{ padding: '0.5rem' }}>Status</th>
              <th style={{ padding: '0.5rem', minWidth: '9rem' }}>Progress</th>
              <th style={{ padding: '0.5rem' }}>Pass req.</th>
              <th style={{ padding: '0.5rem' }} />
            </tr>
          </thead>
          <tbody>
            {batches.map(b => (
              <tr key={b.id} style={{ borderTop: `1px solid ${theme.border}` }}>
                <td style={{ padding: '0.5rem', whiteSpace: 'nowrap' }}>
                  {fmtDateTime(b.t_created)}
                </td>
                <td style={{ padding: '0.5rem' }}>{b.job_name || b.job_id.slice(0, 8)}</td>
                <td style={{ padding: '0.5rem' }}>{b.size}</td>
                <td style={{ padding: '0.5rem' }}><StatusBadge status={b.status} /></td>
                <td style={{ padding: '0.5rem' }}><Progress b={b} /></td>
                <td style={{ padding: '0.5rem' }}>{b.sla_pass_pct}%</td>
                <td style={{ padding: '0.5rem' }}>
                  <Link to={`/tactical/${b.id}`} style={{ color: theme.primary, fontSize: '0.8rem' }}>
                    Report →
                  </Link>
                  {/* Whole-batch only. There is no per-fax delete on purpose: the statistics
                      are only meaningful over the population that was actually sent, so
                      removing one fax would shrink the denominator and make the report
                      describe a different experiment than the one that ran. */}
                  {isAdmin && (
                    <button
                      onClick={() => deleteBatch(b)}
                      disabled={deleting === b.id}
                      title="Delete this batch and every fax in it"
                      style={{
                        marginLeft: '0.7rem', padding: '0.15rem 0.45rem', fontSize: '0.7rem',
                        background: theme.bgAlt, color: '#e06c6c',
                        border: '1px solid #6a2a2a', borderRadius: '4px', cursor: 'pointer',
                      }}
                    >
                      {deleting === b.id ? '…' : 'Delete'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}
